<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import { useForm } from 'vee-validate';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import * as yup from '@/helpers/yup-distilled';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { useAuthStore } from '@/stores';
import { useUserPreferenceStore } from '@/stores';
import { getFriendlyErrorMessage } from '@/helpers';
import Checkbox from 'primevue/checkbox';

const schema = yup.object().shape({
	us7YR3j: yup.string().required('Username is required')
});

const { defineField, errors, isSubmitting, handleSubmit, setErrors } = useForm({
	validationSchema: schema
});

const reset = () => {
	pwEntryPhase.value = false;
	us7YR3j.value = '';
	paJ2kd8.value = '';
	setErrors({ apiError: '' });
};

const onSubmit = handleSubmit((values, { setErrors }) => {
	if (!pwEntryPhase.value) {
		return;
	}

	const { us7YR3j, paJ2kd8 } = values;

	return authStore
		.login(us7YR3j, paJ2kd8)
		.then(async () => {
			await userPreferenceStore.getAllUserPreferences();
			// redirect to previous url or default to home page
			router.push(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		})
		.catch(async (error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
			pwEntryPhase.value = false;
		});
});

const onRequestResetPassword = async () => {
	if (us7YR3j.value) {
		authStore.setLoginForResetPasswordByPasscode(us7YR3j.value);
		await authStore.requestPasscodeForPasswordReset().then(async () => {
			router.push(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		});
	} else {
		router.push(
			`${import.meta.env.VITE_SIGNIN_BASEURL}resetpasswordbypasscode/specifylogin`
		);
	}
};

const startRecoverUserName = () => {
	authStore.signInState = 'recoverUserName';
	router.push(getNextRouteFromNewSigninState(authStore.signInState, route));
};

const submitUserName = () => {
	setErrors({ apiError: '' });
	if (us7YR3j.value) {
		pwEntryPhase.value = true;
		setTimeout(() => {
			try {
				document.getElementById('paJ2kd8Input').focus();
			} catch (e) {}
		}, 100);
	}
};

// CLASSIC ASPIRE SHIM
// attempt to auto refresh token before startup
onMounted(async () => {
	try {
		if (await authStore.refreshToken()) {
			location.replace(location.pathname);
		}
	} catch {
		// just eat it in this case
	}
});
//END CLASSIC ASPIRE SHIM

const route = useRoute();
const authStore = useAuthStore();
const userPreferenceStore = useUserPreferenceStore();

// Top of the flow - clear any stored reset-by-passcode config
authStore.clearLoginForResetPasswordByPasscode();

const isUVisible = ref(false);
const isPVisible = ref(false);
const [us7YR3j] = defineField('us7YR3j');
const [paJ2kd8] = defineField('paJ2kd8');
const pwEntryPhase = ref(false);
</script>

<template>
	<form @submit="onSubmit">
		<div class="p-fluid login-small-form" v-if="!pwEntryPhase">
			<div class="p-field mt-2 mb-8">
				<label class="font-semibold" for="us7YR3j">Username</label>
				<InputText
					v-model="us7YR3j"
					autocomplete="off"
					autofocus
					type="text"
					:invalid="!!errors.us7YR3j"
					:class="isUVisible ? ['mb-2'] : ['mb-2', 'disc']"
				/>
				<div>
					<Checkbox :class="['mr-1']" v-model="isUVisible" binary />
					<label for="isPUisible" @click="isUVisible = !isUVisible"
						>Show username</label
					>
				</div>
			</div>
			<div class="mb-2">
				<Button
					type="submit"
					:disabled="isSubmitting || !us7YR3j"
					label="Submit"
					v-on:click="submitUserName()"
				></Button>
			</div>

			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
		<div class="p-fluid login-small-form" v-if="pwEntryPhase">
			<div class="mb-8 mt-2">
				<label class="font-semibold" for="paJ2kd8">Password</label>
				<InputText
					autocomplete="off"
					v-model="paJ2kd8"
					id="paJ2kd8Input"
					:class="isPVisible ? ['mb-2'] : ['mb-2', 'disc']"
				/>
				<Checkbox :class="['mr-1']" v-model="isPVisible" binary />
				<label for="isPVisible" @click="isPVisible = !isPVisible"
					>Show password</label
				>
			</div>
			<div class="p-fluid mb-2">
				<div class="button-row">
					<div class="submit-button">
						<Button
							:disabled="isSubmitting || !paJ2kd8"
							label="Submit"
							type="submit"
						></Button>
					</div>
					<div class="cancel-button">
						<Button
							:disabled="isSubmitting"
							@click="reset()"
							label="Cancel"
							severity="danger"
						></Button>
					</div>
				</div>
				<div v-if="errors.apiError" class="p-error">
					{{ errors.apiError }}
				</div>
			</div>
		</div>
		<div class="mb-7">
			<div class="mb-2 flex flex-row justify-content-center">
				<span @click="onRequestResetPassword" class="link-span"
					>Forgotten your password, or need to set a new one?</span
				>
			</div>
			<div class="mb-2 flex flex-row justify-content-center">
				<span class="link-span" @click="startRecoverUserName()"
					>Forgotten your username?</span
				>
			</div>
		</div>
	</form>
</template>
<style scoped>
.link-span {
	cursor: pointer;
	text-decoration: underline;
	color: var(--text-color-secondary);
}

.disc {
	font-family: text-security-disc;
	-webkit-text-security: disc;
	line-height: 1.5;
}

.button-row {
	display: flex;
	flex-direction: row;
}

.submit-button {
	flex: 3;
	padding-right: 0.5rem;
}

.cancel-button {
	flex: 1;
}
</style>
